import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { usePoll } from "@/hooks/usePoll";
import { fmtMoney, fmtSigned, fmtPrice, fmtDateTime, pnlClass, SIDE, ORDER_TYPE, ORDER_STATUS, CLOSE_REASON, LEDGER_KIND } from "@/lib/format";
import { Empty, StatusBadge } from "@/components/kit";

const Th = ({ children, r }) => <th className={`py-2.5 pr-4 eyebrow ${r ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, r, cls = "" }) => <td className={`py-3 pr-4 ${r ? "text-right num" : ""} ${cls}`}>{children}</td>;
const Name = ({ p, link }) => link ? <Link to={`/negociar/${p.symbol}`} className="font-semibold hover:text-primary">{p.name || p.symbol}</Link> : <span className="font-semibold">{p.name || p.symbol}</span>;

const EditDialog = ({ pos, onClose, onSaved }) => {
  const [sl, setSl] = useState(pos.stop_loss ?? "");
  const [tp, setTp] = useState(pos.take_profit ?? "");
  const save = async () => {
    try {
      await api.patch(`/trading/positions/${pos.id}`, { stop_loss: sl === "" ? null : Number(sl), take_profit: tp === "" ? null : Number(tp) });
      toast.success("Stop Loss / Take Profit atualizados"); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="edit-sltp-dialog">
        <DialogHeader><DialogTitle>Editar SL/TP — {pos.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label>Stop Loss (vazio = sem SL)</Label><Input type="number" step="any" value={sl} onChange={(e) => setSl(e.target.value)} data-testid="edit-sl-input" /></div>
          <div className="space-y-2"><Label>Take Profit (vazio = sem TP)</Label><Input type="number" step="any" value={tp} onChange={(e) => setTp(e.target.value)} data-testid="edit-tp-input" /></div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="edit-sltp-save">Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StatementDialog = ({ id, onClose }) => {
  const [d, setD] = useState(null);
  usePoll(() => api.get(`/trading/positions/${id}/statement`).then((r) => setD(r.data)), 600000, [id]);
  const p = d?.position;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="statement-dialog">
        <DialogHeader><DialogTitle>Extrato da operação</DialogTitle></DialogHeader>
        {!p ? <Empty text="Carregando…" /> : (
          <div className="space-y-2 text-sm">
            {[["Ativo", `${p.name} (${p.symbol})`], ["Lado", SIDE[p.side]], ["Quantidade", p.quantity], ["Preço de entrada", p.entry_price],
              ["Preço de saída", p.exit_price ?? "—"], ["Abertura", fmtDateTime(p.opened_at)], ["Encerramento", fmtDateTime(p.closed_at)],
              ["Margem", fmtMoney(p.margin)], ["Comissão abertura", fmtMoney(p.open_commission)], ["Comissão fechamento", fmtMoney(p.close_commission)],
              ["Resultado bruto", fmtSigned(p.gross_pnl ?? p.unrealized_pnl)], ["Resultado líquido", fmtSigned(p.net_pnl)], ["Motivo", CLOSE_REASON[p.close_reason] || "—"]]
              .map(([k, v]) => <div key={k} className="flex justify-between border-b border-border py-1.5"><span className="text-muted-foreground">{k}</span><span className="num">{v}</span></div>)}
            <div className="eyebrow pt-3">Lançamentos</div>
            {d.ledger.map((l) => <div key={l.id} className="flex justify-between text-xs"><span>{LEDGER_KIND[l.kind]}</span><span className={`num ${pnlClass(l.amount)}`}>{fmtSigned(l.amount, l.currency || "USD")}</span></div>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const PositionsPanel = ({ symbol, onData, refreshKey = 0, link = false }) => {
  const [open, setOpen] = useState([]);
  const [pending, setPending] = useState([]);
  const [closed, setClosed] = useState([]);
  const [orders, setOrders] = useState([]);
  const [edit, setEdit] = useState(null);
  const [stmt, setStmt] = useState(null);
  const qs = symbol ? `&symbol=${symbol}` : "";

  const load = async () => {
    const [o, p, c, h] = await Promise.all([api.get(`/trading/positions?status=open${qs}`), api.get(`/trading/orders?status=pending${qs}`),
      api.get(`/trading/positions?status=closed${qs}`), api.get(`/trading/orders?${qs.slice(1)}`)]);
    setOpen(o.data); setPending(p.data); setClosed(c.data); setOrders(h.data);
    onData?.({ open: o.data, pending: p.data, closed: c.data });
  };
  usePoll(() => load().catch(() => {}), 3000, [symbol, refreshKey]);

  const closePos = async (id) => {
    try { const { data } = await api.post(`/trading/positions/${id}/close`); toast.success(`Posição encerrada: ${fmtSigned(data.net_pnl)}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = async (id) => {
    try { await api.delete(`/trading/orders/${id}`); toast.success("Ordem cancelada"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="surface p-5 md:p-6" data-testid="positions-panel">
      <Tabs defaultValue="open">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="open" data-testid="tab-open-positions">Abertas ({open.length})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending-orders">Ordens pendentes ({pending.length})</TabsTrigger>
          <TabsTrigger value="closed" data-testid="tab-closed-positions">Encerradas</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-order-history">Histórico de ordens</TabsTrigger>
        </TabsList>
        <div className="overflow-x-auto">
          <TabsContent value="open">
            {open.length === 0 ? <Empty text="Nenhuma posição aberta." testid="open-positions-empty" /> : (
              <table className="w-full text-sm"><thead><tr><Th>Ativo</Th><Th>Lado</Th><Th r>Qtd</Th><Th r>Entrada</Th><Th r>Atual</Th><Th r>SL / TP</Th><Th r>Margem</Th><Th r>Resultado</Th><Th /></tr></thead>
                <tbody>{open.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`open-position-${p.id}`}>
                    <Td><Name p={p} link={link} /></Td><Td cls={p.side === "buy" ? "up" : "down"}>{SIDE[p.side]}</Td><Td r>{p.quantity}</Td>
                    <Td r>{p.entry_price}</Td><Td r>{p.current_price}</Td><Td r>{p.stop_loss ?? "—"} / {p.take_profit ?? "—"}</Td><Td r>{fmtMoney(p.margin)}</Td>
                    <Td r cls={`font-semibold ${pnlClass(p.unrealized_pnl)}`}><span data-testid={`position-pnl-${p.id}`}>{fmtSigned(p.unrealized_pnl)}</span></Td>
                    <Td><div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setEdit(p)} data-testid={`edit-position-${p.id}`}>SL/TP</Button>
                      <Button size="sm" variant="ghost" onClick={() => setStmt(p.id)} data-testid={`statement-position-${p.id}`}>Extrato</Button>
                      <Button size="sm" variant="outline" onClick={() => closePos(p.id)} data-testid={`close-position-${p.id}`}>Fechar</Button>
                    </div></Td>
                  </tr>))}</tbody></table>
            )}
          </TabsContent>
          <TabsContent value="pending">
            {pending.length === 0 ? <Empty text="Nenhuma ordem pendente." /> : (
              <table className="w-full text-sm"><thead><tr><Th>Ativo</Th><Th>Tipo</Th><Th>Lado</Th><Th r>Qtd</Th><Th r>Preço</Th><Th r>Reservado</Th><Th>Criada</Th><Th /></tr></thead>
                <tbody>{pending.map((o) => (
                  <tr key={o.id} className="border-t border-border" data-testid={`pending-order-${o.id}`}>
                    <Td><Name p={o} link={link} /></Td><Td>{ORDER_TYPE[o.order_type]}</Td><Td cls={o.side === "buy" ? "up" : "down"}>{SIDE[o.side]}</Td>
                    <Td r>{o.quantity}</Td><Td r>{o.requested_price}</Td><Td r>{fmtMoney(o.reserved_margin)}</Td><Td cls="text-xs text-muted-foreground">{fmtDateTime(o.created_at)}</Td>
                    <Td><Button size="sm" variant="outline" onClick={() => cancel(o.id)} data-testid={`cancel-order-${o.id}`}>Cancelar</Button></Td>
                  </tr>))}</tbody></table>
            )}
          </TabsContent>
          <TabsContent value="closed">
            {closed.length === 0 ? <Empty text="Nenhuma operação encerrada." /> : (
              <table className="w-full text-sm"><thead><tr><Th>Ativo</Th><Th>Lado</Th><Th r>Qtd</Th><Th r>Entrada</Th><Th r>Saída</Th><Th>Motivo</Th><Th r>Líquido</Th><Th>Encerrada</Th><Th /></tr></thead>
                <tbody>{closed.map((p) => (
                  <tr key={p.id} className="border-t border-border" data-testid={`closed-position-${p.id}`}>
                    <Td><Name p={p} link={link} /></Td><Td>{SIDE[p.side]}</Td><Td r>{p.quantity}</Td><Td r>{p.entry_price}</Td><Td r>{p.exit_price}</Td>
                    <Td>{CLOSE_REASON[p.close_reason]}</Td><Td r cls={`font-semibold ${pnlClass(p.net_pnl)}`}>{fmtSigned(p.net_pnl)}</Td>
                    <Td cls="text-xs text-muted-foreground">{fmtDateTime(p.closed_at)}</Td>
                    <Td><Button size="sm" variant="ghost" onClick={() => setStmt(p.id)} data-testid={`statement-closed-${p.id}`}>Extrato</Button></Td>
                  </tr>))}</tbody></table>
            )}
          </TabsContent>
          <TabsContent value="orders">
            {orders.length === 0 ? <Empty text="Nenhuma ordem." /> : (
              <table className="w-full text-sm"><thead><tr><Th>Data</Th><Th>Ativo</Th><Th>Tipo</Th><Th>Lado</Th><Th r>Qtd</Th><Th r>Preço</Th><Th r>Execução</Th><Th>Status</Th></tr></thead>
                <tbody>{orders.map((o) => (
                  <tr key={o.id} className="border-t border-border" data-testid={`order-row-${o.id}`}>
                    <Td cls="text-xs text-muted-foreground">{fmtDateTime(o.created_at)}</Td><Td><Name p={o} link={link} /></Td><Td>{ORDER_TYPE[o.order_type]}</Td><Td>{SIDE[o.side]}</Td>
                    <Td r>{o.quantity}</Td><Td r>{o.requested_price ?? "—"}</Td><Td r>{o.fill_price ? fmtPrice(o.fill_price, 5).replace(/0+$/, "").replace(/,$/, "") : "—"}</Td>
                    <Td><StatusBadge map={ORDER_STATUS} value={o.status} />{o.reject_reason && <div className="text-xs down mt-1">{o.reject_reason}</div>}</Td>
                  </tr>))}</tbody></table>
            )}
          </TabsContent>
        </div>
      </Tabs>
      {edit && <EditDialog pos={edit} onClose={() => setEdit(null)} onSaved={load} />}
      {stmt && <StatementDialog id={stmt} onClose={() => setStmt(null)} />}
    </div>
  );
};
